/**
 * Компонент графиков посещаемости - Enterprise Design v2.0
 * QRIUM Recvisit
 */
import { useState, useEffect, memo, useCallback } from 'react'
import { EventsRepo } from '../offline/eventsRepo'
import type { EventRecord as Event } from '../offline/db'

export const Charts = memo(function Charts() {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
  const [events, setEvents] = useState<Event[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const loadEvents = useCallback(async () => {
    setIsLoading(true)
    try {
      const allEvents = await EventsRepo.getAll({ limit: 10000 })
      const date = new Date(selectedDate)
      
      const dayEvents = allEvents.filter((event) => {
        const eventDate = new Date(event.happened_at)
        return (
          eventDate.getDate() === date.getDate() &&
          eventDate.getMonth() === date.getMonth() &&
          eventDate.getFullYear() === date.getFullYear()
        )
      })

      setEvents(dayEvents)
    } catch (error) {
      console.error('Failed to load events:', error)
    } finally {
      setIsLoading(false)
    }
  }, [selectedDate])

  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  const calculatePeopleOnSite = useCallback(() => {
    const hourlyData: Record<number, number> = {}
    for (let hour = 0; hour < 24; hour++) {
      hourlyData[hour] = 0
    }

    const sortedEvents = [...events].sort(
      (a, b) => new Date(a.happened_at).getTime() - new Date(b.happened_at).getTime()
    )

    let currentCount = 0
    sortedEvents.forEach((event) => {
      const hour = new Date(event.happened_at).getHours()
      
      if (event.event_type === 'check_in') {
        currentCount++
      } else if (event.event_type === 'check_out') {
        currentCount = Math.max(0, currentCount - 1)
      }

      for (let h = hour; h < 24; h++) {
        hourlyData[h] = currentCount
      }
    })

    return hourlyData
  }, [events])

  const calculateInOutFlow = useCallback(() => {
    const hourlyData: Record<number, { in: number; out: number }> = {}
    
    for (let hour = 0; hour < 24; hour++) {
      hourlyData[hour] = { in: 0, out: 0 }
    }

    events.forEach((event) => {
      const hour = new Date(event.happened_at).getHours()
      if (event.event_type === 'check_in') {
        hourlyData[hour].in++
      } else if (event.event_type === 'check_out') {
        hourlyData[hour].out++
      }
    })

    return hourlyData
  }, [events])

  const peopleOnSite = calculatePeopleOnSite()
  const inOutFlow = calculateInOutFlow()
  const maxPeople = Math.max(...Object.values(peopleOnSite), 1)
  const maxFlow = Math.max(...Object.values(inOutFlow).flatMap((v) => [v.in, v.out]), 1)

  const formatDateLabel = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    if (date.toDateString() === today.toDateString()) {
      return 'Сегодня'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Вчера'
    }
    
    return date.toLocaleDateString('ru-RU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
  }

  const checkIns = events.filter((e) => e.event_type === 'check_in').length
  const checkOuts = events.filter((e) => e.event_type === 'check_out').length
  const maxOnSite = Math.max(...Object.values(peopleOnSite))

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="loading-spinner mb-4" />
        <p className="text-[#a0a0a0] text-[14px]">Загрузка данных...</p>
      </div>
    )
  }

  return (
    <div className="space-y-section">
      {/* Date Picker */}
      <div className="focus-block">
        <div className="flex items-center justify-between" style={{gap: '16px'}}>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-[#6b6b6b] uppercase tracking-wide font-bold" style={{marginBottom: '6px'}}>Выбранная дата</p>
            <p className="text-[16px] font-semibold text-white truncate">
              {formatDateLabel(selectedDate)}
            </p>
          </div>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-[#2a2a2a] border-2 border-[#3a3a3a] rounded-xl text-white text-[14px] font-medium cursor-pointer"
            style={{
              padding: '12px 16px',
              minHeight: '48px',
              minWidth: '160px',
              colorScheme: 'dark'
            }}
          />
        </div>
      </div>

      {/* Stats - 3 columns */}
      <div className="stats-grid-3">
        <div className="stat-card">
          <div className="stat-card-value-medium text-[#22c55e]">{checkIns}</div>
          <div className="stat-card-label">Приходов</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value-medium text-[#ef4444]">{checkOuts}</div>
          <div className="stat-card-label">Уходов</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value-medium text-[#3b82f6]">{maxOnSite}</div>
          <div className="stat-card-label">Макс. на месте</div>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📊</div>
          <p className="empty-state-title">Нет данных</p>
          <p className="empty-state-description">За выбранную дату событий не найдено</p>
        </div>
      ) : (
        <>
          {/* Chart: People on Site */}
          <div className="chart-container">
            <div className="flex items-center justify-between mb-4">
              <span className="section-title">Люди на объекте</span>
              <span className="text-[12px] text-[#6b6b6b]">По часам</span>
            </div>
            <div className="chart-canvas flex items-end gap-[2px]">
              {Object.entries(peopleOnSite).map(([hour, count]) => {
                const height = maxPeople > 0 ? (count / maxPeople) * 100 : 0
                const showLabel = parseInt(hour) % 4 === 0
                
                return (
                  <div key={hour} className="flex-1 flex flex-col items-center">
                    <div className="w-full flex items-end justify-center h-[100px]">
                      <div
                        className="w-full bg-[#3b82f6] rounded-t-[2px] transition-all duration-200"
                        style={{ 
                          height: `${height}%`, 
                          minHeight: count > 0 ? '3px' : '1px',
                          opacity: count > 0 ? 1 : 0.15
                        }}
                      />
                    </div>
                    {showLabel && (
                      <span className="text-[10px] text-[#6b6b6b] mt-2 tabular-nums">{hour}:00</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Chart: In/Out Flow */}
          <div className="chart-container">
            <div className="flex items-center justify-between mb-4">
              <span className="section-title">Приход / Уход</span>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-[#22c55e]" />
                  <span className="text-[11px] text-[#a0a0a0]">Приход</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-[#ef4444]" />
                  <span className="text-[11px] text-[#a0a0a0]">Уход</span>
                </div>
              </div>
            </div>
            <div className="chart-canvas flex items-end gap-[2px]">
              {Object.entries(inOutFlow).map(([hour, data]) => {
                const inHeight = maxFlow > 0 ? (data.in / maxFlow) * 100 : 0
                const outHeight = maxFlow > 0 ? (data.out / maxFlow) * 100 : 0
                const showLabel = parseInt(hour) % 4 === 0
                
                return (
                  <div key={hour} className="flex-1 flex flex-col items-center">
                    <div className="w-full flex items-end justify-center gap-[1px] h-[100px]">
                      <div
                        className="flex-1 bg-[#22c55e] rounded-t-[2px] transition-all duration-200"
                        style={{ 
                          height: `${inHeight}%`, 
                          minHeight: data.in > 0 ? '3px' : '1px',
                          opacity: data.in > 0 ? 1 : 0.15
                        }}
                      />
                      <div
                        className="flex-1 bg-[#ef4444] rounded-t-[2px] transition-all duration-200"
                        style={{ 
                          height: `${outHeight}%`, 
                          minHeight: data.out > 0 ? '3px' : '1px',
                          opacity: data.out > 0 ? 1 : 0.15
                        }}
                      />
                    </div>
                    {showLabel && (
                      <span className="text-[10px] text-[#6b6b6b] mt-2 tabular-nums">{hour}:00</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
})
